import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { openPDF } from '@avanta/shared/utils/storage';
import { Calendar, FileText, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { type Appointment } from 'types/appointments';
import { type Report } from 'types/files';
import { isCompletedAppointment } from '../../utils/appointments';

interface SessionTableActionsProps {
  appointment: Appointment;
  reports: Report[];
}

const downloadReport = (report: Report): void => {
  const binaryString = atob(report.pdf);
  const byteArray = Uint8Array.from(binaryString, char => char.charCodeAt(0));
  const file = new Blob([byteArray], { type: 'application/pdf' });
  openPDF({
    file,
    filename: `${report.id}-${report.type}`,
  });
};

export const SessionTableActions = ({
  appointment,
  reports,
}: SessionTableActionsProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const navigate = useNavigate();
  const isCompleted = isCompletedAppointment(appointment);

  const handleViewAppointment = (): void => {
    navigate(generatePath(ROUTES.PATIENT_APPOINTMENT, { id: appointment.patientId }));
  };

  const handleViewReport = (): void => {
    const report = reports.find(item => item.pdf);
    if (!report) {
      toast.error(t('ReportsHistory.empty'));
      return;
    }
    downloadReport(report);
  };

  if (appointment.canceled) {
    return (
      <Button variant="terciary" icon="leading" className="w-fit" onClick={handleViewAppointment}>
        <Info />
        {t('AppointmentsHistory.actions.details')}
      </Button>
    );
  }

  if (isCompleted) {
    return (
      <Button variant="terciary" icon="leading" className="w-fit" onClick={handleViewReport}>
        <FileText />
        {t('AppointmentsHistory.actions.report')}
      </Button>
    );
  }

  return (
    <Button variant="terciary" icon="leading" className="w-fit" onClick={handleViewAppointment}>
      <Calendar />
      {t('InsuredPersonsList.manageAppointment.cta')}
    </Button>
  );
};
